import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination,
  MenuItem,
  Menu,
  useTheme,
  Tooltip,
  Divider,
  Backdrop,
  CircularProgress,
  Button
} from "@mui/material";
import {
  FilterList,
  Print,
  ArrowBack,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  Check as CheckIcon,
  Circle as CircleIcon
} from "@mui/icons-material";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { successToster, errorToster, infoToster } from "../../shared/toster/Toster";
import { useTranslation } from "react-i18next";
import SearchBar from "../../components/common-components/Search-bar";
import { Helper } from "../../utils/Helpers";
import { ActionGuard } from "../../core/permissions";

import { 
  TASK_REPORT_ROWS_PER_PAGE, 
  FILTER_STATUS_OPTIONS,   
  FILTER_PRIORITY_OPTIONS  
} from "../../utils/Constants"; 


const COMMON_ICON_BUTTON_STYLES = {
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  width: { xs: "36px", md: "2.5vw" }, 
  height: { xs: "36px", md: "2.5vw" },
  border: "1px solid #F7A86E",
  color: "#F7A86E",
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  transition: "all 0.2s ease-in-out",
  "&:hover": { 
    backgroundColor: "#F7A86E",
    color: "#FFFFFF",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 4px rgba(247, 168, 110, 0.2)"
  },
};

const COMMON_CELL_SX = {
  textAlign: "left",
  color: "#474747",
  borderBottom: "1px solid rgba(246, 210, 127, 0.3)",
};

const PRINT_STYLES = `
    @media print {
      body * { visibility: hidden; }
      #printable-area, #printable-area * { visibility: visible; }
      #printable-area { position: absolute; left: 0; top: 0; width: 100%; background: white; }
      #screen-table-container { display: none !important; }
      #print-table-container { display: block !important; }
      .no-print { display: none !important; }
      td, th { word-wrap: break-word; word-break: break-word; white-space: normal !important; }
    }
`;

const EmployeeTaskDownload = ({ handleBack }) => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const theme = useTheme();
  const { palette } = theme;
  const helper = new Helper();

  const [tasks, setTasks] = useState([]); 
  const [printTasks, setPrintTasks] = useState([]); 
  
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false); 

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0); 
  const rowsPerPage = TASK_REPORT_ROWS_PER_PAGE; 

  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  const headers = [
    { label: t("pages.guest_history_page.table.serial_number"), id: "sl" },
    { label: t("pages.employee.table.name"), id: "name" },
    { label: t("pages.charges_posting.table.description"), id: "desc" },
    { label: "Created Date", id: "created" },
    { label: "Due Date", id: "due" },
    { label: t("pages.task_management.completed"), id: "completed" },
    { label: t("pages.dynamic_pricing.table.status"), id: "status", hasFilter: true },
    { label: t("pages.dynamic_pricing.table.priority"), id: "priority", hasFilter: true },
  ];

  useEffect(() => {
    setPage(1);
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    fetchTasks();
  }, [page, statusFilter, priorityFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        fetchTasks();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchName]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      let query = `${END_POINT.EMPLOYEE.GET_ALL_TASKS}?page=${page}&limit=${rowsPerPage}`;
      if (searchName) query += `&search=${searchName}`;
      if (statusFilter !== "All") query += `&status=${statusFilter}`;
      if (priorityFilter !== "All") query += `&priority=${priorityFilter}`;

      const response = await api.get(query);
      if (response.status === 200) {
        setTasks(response.data.data.data);
        setTotalRecords(response.data.data.total);
        setTotalPages(Math.ceil(response.data.data.total / rowsPerPage));
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataForExport = async () => {
    const downloadLimit = totalRecords > 0 ? totalRecords : 100000; 
    let query = `${END_POINT.EMPLOYEE.GET_ALL_TASKS}?page=1&limit=${downloadLimit}`; 
    
    if (searchName) query += `&search=${searchName}`;
    if (statusFilter !== "All") query += `&status=${statusFilter}`;
    if (priorityFilter !== "All") query += `&priority=${priorityFilter}`;

    const response = await api.get(query);
    if (response.status === 200) {
      return response.data?.data?.data || [];
    }
    return [];
  };

  const handlePrintClick = async () => {
    setIsPrinting(true); 
    try {
      const allData = await fetchDataForExport();
      
      if (allData.length === 0) {
        errorToster(t("pages.charges_posting.no_data_to_export"));
        setIsPrinting(false);
        return;
      }

      setPrintTasks(allData); 

      setTimeout(() => {
        window.print();
        setIsPrinting(false); 
      }, 500);

    } catch (error) {
      console.error("Print Fetch Error:", error);
      errorToster("Failed to prepare print document");
      setIsPrinting(false);
    }
  };

  const handleDownload = async () => {
    infoToster("Downloading all data...");
    try {
      const allData = await fetchDataForExport(); 

      if (allData.length === 0) {
        errorToster(t("pages.charges_posting.no_data_to_export"));
        return;
      }
      
      const headerString = `${t("pages.guest_history_page.table.serial_number")},${t("pages.employee.table.name")},${t("pages.charges_posting.table.description")},Created,Due,${t("pages.task_management.completed")},${t("pages.dynamic_pricing.table.status")},${t("pages.dynamic_pricing.table.priority")}`;
      const csvHeaders = [headerString];
      
      const csvRows = allData.map((task, index) => {
        const slNo = index + 1;
        const assigneeName = Array.isArray(task.assigned_to_name) ? task.assigned_to_name.join(', ') : (task.assigned_to_name || "Unassigned");
        const assignee = `"${assigneeName}"`;
        const rawDesc = task.task_description || "";
        const desc = `"${rawDesc.replace(/"/g, '""')}"`;

        const created = helper.formatDateForReport(task.assigned_at) + " " + helper.formatTimeForReport(task.assigned_at);
        const due = helper.formatDateForReport(task.due_at) + " " + helper.formatTimeForReport(task.due_at);
        const completed = (task.status === "Done" || task.status === "Completed") && task.completed_at
          ? helper.formatDateForReport(task.completed_at) + " " + helper.formatTimeForReport(task.completed_at) : "-";
        
        return `${slNo},${assignee},${desc},${created},${due},${completed},${task.status},${task.priority}`;
      });

      const csvString = [csvHeaders, ...csvRows].join("\n");
      const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvString);
      
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `All_Tasks_Report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      successToster("Exported Successfully");
    } catch (error) {
      console.error("Download Error:", error);
      errorToster("Failed to download data");
    }
  };


  const responsiveCellSx = {
    ...COMMON_CELL_SX,
    fontSize: { xs: "13px", sm: "14px", md: "0.85vw" }, 
    padding: { xs: "12px 8px", sm: "14px 12px", md: "12px 1vw" },
    whiteSpace: "normal", 
    wordBreak: "break-word", 
  };

  const responsiveHeaderSx = {
    background: palette.tab?.gradient || "linear-gradient(to right, #FFD194, #D1913C)",
    color: "#ffffff", 
    fontWeight: 600, 
    whiteSpace: "nowrap",
    py: { xs: "12px", sm: "14px", md: "1vw" },
    px: { xs: "10px", sm: "12px", md: "1vw" },
    fontSize: { xs: "14px", sm: "15px", md: "0.9vw" },
  };

  const printCellSx = {
    fontSize: "10pt", 
    padding: "4px 8px",
    borderBottom: "1px solid #ddd",
    color: "#000",
    whiteSpace: "normal", 
    wordWrap: "break-word" 
  };

  const printHeaderSx = {
    fontSize: "10pt",
    fontWeight: "bold",
    backgroundColor: "#eee",
    color: "#000",
    padding: "6px",
    borderBottom: "2px solid #333"
  };

  const handleFilterClick = (event, column) => {
    setAnchorEl(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleFilterSelect = (value) => {
    if (activeFilterColumn === "status") setStatusFilter(value);
    else if (activeFilterColumn === "priority") setPriorityFilter(value);
    handleFilterClose();
  };

  const renderFilterMenuItems = () => {
    const isStatus = activeFilterColumn === "status";
    const options = isStatus ? FILTER_STATUS_OPTIONS : FILTER_PRIORITY_OPTIONS;
    const currentFilter = isStatus ? statusFilter : priorityFilter;

    return options.map((option) => {
      const isSelected = currentFilter === option;
      let optionColor = "#444";
      let optionBg = "transparent";

      if (option !== 'All') {
        if (isStatus) {
            const style = helper.getStatusStyle(option);
            if (style) {
                optionColor = style.color;
                optionBg = style.bgcolor;
            }
        } else {
            const pColor = helper.getPriorityColor(option);
            optionColor = pColor;
            optionBg = `${pColor}1A`;
        }
      }

      const menuItemSx = {
        mx: "2%", my: "1%", borderRadius: "8px",
  
        fontSize: { xs: "4vw", sm: "1.5rem", md: "0.85vw" }, 
        fontWeight: isSelected ? "bold" : "normal",
        bgcolor: isSelected ? (optionBg !== "transparent" ? optionBg : "#FFF3E0") : "transparent",
        color: optionColor,
        border: isSelected ? `1px solid ${optionColor !== "#444" ? optionColor : "#F7A86E"}` : "1px solid transparent",
        "&:hover": { bgcolor: optionBg !== "transparent" ? optionBg : "#f5f5f5" },
        display: "flex", justifyContent: "space-between", px: "3%"
      };

      return (
        <MenuItem key={option} onClick={() => handleFilterSelect(option)} sx={menuItemSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5%" }}>
                {option !== "All" && <CircleIcon sx={{ fontSize: { xs: "3.5vw", md: "0.8vw" }, color: optionColor || "#ccc" }} />}
                {isStatus ? helper.getStatusLabel(option, t) : option}
            </Box>
            {isSelected && <CheckIcon fontSize="small" sx={{ color: optionColor || "inherit" }} />}
        </MenuItem>
      );
    });
  };

  const renderRow = (task, index, isPrint = false) => {
    const sx = isPrint ? printCellSx : responsiveCellSx;
    const slNo = isPrint ? index + 1 : (page - 1) * rowsPerPage + index + 1;

    return (
      <TableRow key={task.task_id} hover={!isPrint}>
        <TableCell sx={sx}>{slNo}</TableCell>
        <TableCell sx={sx}>
          {Array.isArray(task.assigned_to_name) ? task.assigned_to_name.join(", ") : task.assigned_to_name || "Unassigned"}
        </TableCell>
        <TableCell sx={{ ...sx, minWidth: { xs: "180px", sm: "auto" } }}> 
          {/* Increased minWidth for description */}
          {!isPrint ? (
              <Tooltip title={task.task_description} arrow>
                  <Typography variant="body2" sx={{ fontSize: "inherit", whiteSpace: "normal", wordBreak: "break-word" }}>
                    {task.task_description}
                  </Typography>
              </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "inherit", whiteSpace: "normal", wordBreak: "break-word" }}>
              {task.task_description}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ ...sx, whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight="bold" fontSize="inherit">{helper.formatDateForReport(task.assigned_at)}</Typography>
          <Typography variant="caption" color="textSecondary" fontSize={isPrint ? "8pt" : "0.75rem"}>{helper.formatTimeForReport(task.assigned_at)}</Typography>
        </TableCell>
        <TableCell sx={{ ...sx, whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight="bold" fontSize="inherit">{helper.formatDateForReport(task.due_at)}</Typography>
          <Typography variant="caption" color="textSecondary" fontSize={isPrint ? "8pt" : "0.75rem"}>{helper.formatTimeForReport(task.due_at)}</Typography>
        </TableCell>
        <TableCell sx={{ ...sx, whiteSpace: "nowrap" }}>
          {(task.status === "Done" || task.status === "Completed") && task.completed_at ? (
            <><Typography variant="body2" fontWeight="bold" fontSize="inherit">{helper.formatDateForReport(task.completed_at)}</Typography>
              <Typography variant="caption" color="textSecondary" fontSize={isPrint ? "8pt" : "0.75rem"}>{helper.formatTimeForReport(task.completed_at)}</Typography></>
          ) : (<Typography variant="body2" color="textSecondary" fontSize="inherit">-</Typography>)}
        </TableCell>
        <TableCell sx={sx}>
          {isPrint ? (
             <Typography variant="body2" fontWeight="bold" fontSize="inherit">{helper.getStatusLabel(task.status, t)}</Typography>
          ) : (
             <Chip label={helper.getStatusLabel(task.status, t)} size="small" sx={{ ...helper.getStatusStyle(task.status), fontWeight: "bold", borderRadius: "4px", minWidth: "90px", height: "28px", fontSize: "0.8rem" }} />
          )}
        </TableCell>
        <TableCell sx={{ ...sx, color: !isPrint && helper.getPriorityColor(task.priority), fontWeight: "bold" }}>{task.priority}</TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{
      border: palette.border?.gradient ? `1px solid ${palette.border.gradient}` : "1px solid #DAA520",
      borderRadius: "8px", mt: "0.5%", bgcolor: "#fff", height: { xs: "88vh", sm: "85vh", md: "80vh" }, display: "flex", flexDirection: "column" 
    }}>
      <style>{PRINT_STYLES}</style>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isPrinting}>
         <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>Preparing Print Document...</Typography>
         </Box>
      </Backdrop>

      <Box className="no-print" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: { xs: "12px 16px", md: "1.5% 2%" }, flexShrink: 0 }}>
        <Typography variant="h6" fontWeight="bold" color="#DAA520" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.4vw" } }}>
          {t("pages.employee.employee_task_report")}
        </Typography>
      </Box>


      <Box className="no-print" sx={{
        bgcolor: "#FFFBF2", 
        border: "1px solid rgba(218, 165, 32, 0.3)",
        mx: { xs: "12px", md: "2%" },
        mb: "12px",
        p: { xs: 1.5, sm: 2 },
        borderRadius: "12px",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "stretch",
        gap: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
      }}>
       
        <Box sx={{ flex: { xs: "1 1 auto", sm: "0 1 400px" }, minWidth: { sm: "250px" } }}>
          <SearchBar 
            startAdornment={true} 
            placeholder={t("pages.task_management.search_placeholder")} 
            height={{ xs: "40px", md: "2.8vw" }} 
            borderColor="rgba(218, 165, 32, 0.4)" 
            setSearchKey={setSearchName} 
            searchKey={searchName} 
            handleSearch={fetchTasks} 
          />
        </Box>
        

        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: { xs: "space-between", sm: "flex-end" }, 
          gap: { xs: 1, sm: 1.5 },
          flexGrow: { sm: 1 }
        }}>
          <Box sx={{ display: "flex", gap: { xs: 1, sm: 1.5 }, alignItems: "center" }}>
            <ActionGuard action="export">
              <Tooltip title={t("pages.reports.download")}>
                <IconButton size="small" sx={COMMON_ICON_BUTTON_STYLES} onClick={handleDownload}>
                  <FileDownloadOutlinedIcon sx={{ fontSize: { xs: "20px", md: "1.4vw" } }} />
                </IconButton>
              </Tooltip>
            </ActionGuard>
            <ActionGuard action="export">
              <Tooltip title={t("pages.reports.print")}>
                <IconButton size="small" sx={COMMON_ICON_BUTTON_STYLES} onClick={handlePrintClick}>
                  <Print sx={{ fontSize: { xs: "20px", md: "1.4vw" } }} />
                </IconButton>
              </Tooltip>
            </ActionGuard>
          </Box>

          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBack sx={{ fontSize: { xs: "18px !important", md: "1.2vw !important" } }} />}
            sx={{
              borderColor: "#F7A86E", 
              color: "#F7A86E",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "13px", sm: "14px", md: "0.85vw" }, 
              height: { xs: "36px", md: "2.5vw" }, 
              borderRadius: "8px",
              px: { xs: 2, md: 2.5 },
              whiteSpace: "nowrap",
              "&:hover": { 
                borderColor: "#F7A86E", 
                backgroundColor: "#FFF8E1",
                boxShadow: "0 2px 4px rgba(247, 168, 110, 0.1)"
              }
            }}
          >
            {t("common.back")}
          </Button>
        </Box>
      </Box>

      <div id="printable-area" style={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", margin: "0 2% 16px 2%" }}>
        <div id="screen-table-container" style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", borderRadius: "12px", border: "1px solid rgba(218, 165, 32, 0.3)", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <TableContainer component={Paper} elevation={0} sx={{
              maxHeight: "100%", overflowY: "auto", 
              borderRadius: "12px",
              "&::-webkit-scrollbar": { width: "6px", height: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(218, 165, 32, 0.2)", borderRadius: "10px" }
            }}>
              <Table stickyHeader sx={{ minWidth: { xs: "750px", sm: "900px" } }}> {/* Increased minWidth for scroll */}
                <TableHead>
                  <TableRow>
                    {headers.map((head, index) => {
                      const isActive = (head.id === "status" && statusFilter !== "All") || (head.id === "priority" && priorityFilter !== "All");
                      return (
                        <TableCell key={head.id} sx={{ ...responsiveHeaderSx, ...(index === 0 && { position: "sticky", left: 0, zIndex: 3 }) }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5vw" }}>
                            {head.label}
                            {head.hasFilter && (
                              <Tooltip title="Filter">
                                <IconButton size="small" onClick={(e) => handleFilterClick(e, head.id)} sx={{ color: isActive ? "#FFD700" : "#FFFFFF", bgcolor: isActive ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.2)", border: `1px solid ${isActive ? "#FFD700" : "rgba(255, 255, 255, 0.6)"}`, borderRadius: "4px", p: "2px", ml: "5%", "&:hover": { bgcolor: "rgba(0, 0, 0, 0.4)", border: "1px solid #FFFFFF" } }}>
                                  <FilterList sx={{ fontSize: "1.2rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, ...responsiveCellSx }}>Loading...</TableCell></TableRow> : tasks.map((task, i) => renderRow(task, i, false))}
                </TableBody>
              </Table>
            </TableContainer>
        </div>

        <div id="print-table-container" style={{ display: "none" }}>
             <Box sx={{ mb: 2, textAlign: 'center', borderBottom: '2px solid #ccc', pb: 1 }}>
                <Typography variant="h5" fontWeight="bold">Employee Task Report</Typography>
                <Typography variant="subtitle2">Generated on: {new Date().toLocaleDateString()}</Typography>
             </Box>
             <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                <TableHead>
                    <TableRow>
                        {headers.map(head => (
                             <TableCell key={head.id} sx={printHeaderSx}>{head.label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {printTasks.length > 0 && printTasks.map((task, i) => renderRow(task, i, true))}
                </TableBody>
             </Table>
        </div>
      </div>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleFilterClose} PaperProps={{ elevation: 5, sx: { mt: "1%", borderRadius: "12px", minWidth: "15vw", border: `1px solid ${palette.border?.gradient || "#FFE097"}`, p: "1%" } }}>
        <Typography variant="caption" sx={{ px: "5%", pb: "2%", display: 'block', color: "#999", fontWeight: "bold", textTransform: "uppercase", fontSize: { xs: "4vw", md: "0.7vw" } }}>
          Filter by {activeFilterColumn === "status" ? "Status" : "Priority"}
        </Typography>
        <Divider sx={{ mb: "2%" }} />
        {renderFilterMenuItems()}
      </Menu>

      <Box className="no-print" sx={{ 
        display: "flex", 
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between", 
        alignItems: "center", 
        p: { xs: 2, md: 2 }, 
        gap: { xs: 2, sm: 0 },
        borderTop: "1px solid rgba(0,0,0,0.05)",
        bgcolor: "#fff",
        flexShrink: 0 
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: "13px", md: "0.85vw" } }}>
          {t("common.showing")} {Math.min((page - 1) * rowsPerPage + 1, totalRecords)} - {Math.min(page * rowsPerPage, totalRecords)} {t("common.of")} {totalRecords} {t("pages.task_management.tasks")}
        </Typography>
        <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(e, v) => setPage(v)} 
            shape="rounded" 
            size={window.innerWidth < 600 ? "small" : "medium"}
            variant="outlined" 
            color="primary" 
            showFirstButton 
            showLastButton 
            sx={{ 
              "& .Mui-selected": { bgcolor: palette.primary?.main + " !important", color: "white !important" },
              "& .MuiPagination-ul": { justifyContent: "center" }
            }} 
        />
      </Box>
    </Box>
  );
};

export default EmployeeTaskDownload;